import { en, vi, type Locale, type Messages } from './dict';

export const LOCALES: readonly Locale[] = ['vi', 'en'];
export const DEFAULT_LOCALE: Locale = 'vi';
const KEY = 'hana.locale';

const tables: Record<Locale, Messages> = { vi: { ...vi }, en: { ...en } };
const listeners = new Set<() => void>();
let current: Locale = DEFAULT_LOCALE;

export function isLocale(v: unknown): v is Locale {
  return v === 'vi' || v === 'en';
}

/** Vùng thêm khoá của mình; thiếu bản EN thì rơi về VI. */
export function addMessages(locale: Locale, m: Messages): void {
  Object.assign(tables[locale], m);
}

export function getLocale(): Locale {
  return current;
}

export function setLocale(l: Locale): void {
  if (l === current) return;
  current = l;
  try {
    localStorage.setItem(KEY, l);
  } catch {
    /* chế độ riêng tư: bỏ qua */
  }
  if (typeof document !== 'undefined') document.documentElement.lang = l;
  listeners.forEach((f) => f());
}

/** Đọc ngôn ngữ đã lưu (gọi một lần khi khởi động). */
export function initLocale(): Locale {
  try {
    const s = localStorage.getItem(KEY);
    if (isLocale(s)) current = s;
  } catch {
    /* bỏ qua */
  }
  if (typeof document !== 'undefined') document.documentElement.lang = current;
  return current;
}

export function subscribe(f: () => void): () => void {
  listeners.add(f);
  return () => listeners.delete(f);
}

export type Vars = Record<string, string | number>;

export function translate(locale: Locale, key: string, vars?: Vars): string {
  const raw = tables[locale][key] ?? tables.vi[key] ?? key;
  return vars ? raw.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m)) : raw;
}

export const t = (key: string, vars?: Vars): string => translate(current, key, vars);

const intl = (l: Locale) => (l === 'vi' ? 'vi-VN' : 'en-GB');

export function formatNumber(n: number, locale: Locale = current, o?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(intl(locale), o).format(n);
}

/** Ngày dd/MM/yyyy cho cả hai ngôn ngữ (mã ngày nghiệp vụ giữ một dạng). */
export function formatDate(d: Date | string | number, locale: Locale = current, o?: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(intl(locale), o ?? { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(d));
}
